/** Identity + targeting card for the notification editor.
 *
 * Name and language policy sit on top; the Targeting accordion below is FULLY
 * editable (event, triggered-by roles + guests/observers, booking state, tour
 * override mode + tours, delivery lock, user-prefs override, tags, access
 * tags) and shows a chips summary of the DRAFT state while collapsed. It
 * auto-expands when a save 400 lands on one of its fields.
 *
 * Wire notes: guests/observers are the `apply_to_guests`/`apply_to_observers`
 * booleans (the dashboard models them as pseudo-role entries — deliberately
 * not copied); all M2Ms are id lists; `timedelta` (event timing) stays
 * read-only. Roles/access-tag lookups are tenant-scoped — system templates
 * show those pickers disabled.
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as languagePoliciesApi from '@/api/languagePolicies';
import type { NotificationCore, NotificationDetail } from '@/api/notifications';
import { BOOKING_STATE_LABELS, TOUR_OVERRIDE_MODE_LABELS } from '@/api/notifications';
import * as notificationsApi from '@/api/notifications';
import { rbacApi } from '@/api/rbac';
import * as tagsApi from '@/api/tags';
import * as toursApi from '@/api/tours';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';

import { TARGETING_HELP } from './fieldHelp';
import { languagePolicyActions, roleActions, tourActions } from './notificationLinks';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { eventHasBookingState, humanizeSeconds, targetingSummary } from './targetingSummary';
import type { NotificationDraft } from './useNotificationDraft';

interface PolicyOption {
  id: number;
  codename: string;
}

interface IdOption {
  id: number;
  name: string;
}

const TOUR_SPECIFIC_MODE = 2;
const MAX_LOCK_TIME = 32767;

function timedeltaLabel(timedelta: number | null): string {
  if (timedelta == null || timedelta === 0) return 'immediately (no offset)';
  const human = humanizeSeconds(timedelta);
  return timedelta < 0 ? `${human} before the event` : `${human} after the event`;
}

/** Keeps display labels for id-valued multiselects across discard/restore:
 *  labels learned from refs + past picks survive; unknown ids render as #id. */
function useLabelledIds(ids: number[] | undefined, initial: IdOption[]) {
  const [labels, setLabels] = useState<Map<number, string>>(
    () => new Map(initial.map((o) => [o.id, o.name])),
  );
  const value = useMemo(
    () => (ids ?? []).map((id) => ({ id, name: labels.get(id) ?? `#${id}` })),
    [ids, labels],
  );
  const learn = useCallback((options: IdOption[]) => {
    setLabels((prev) => {
      const next = new Map(prev);
      for (const o of options) next.set(o.id, o.name);
      return next;
    });
  }, []);
  return { value, learn };
}

const TARGETING_ERROR_KEYS = [
  'event', 'roles', 'apply_to_guests', 'apply_to_observers', 'booking_state',
  'tour_override_mode', 'tours', 'ignore_user_nf_prefs', 'lock_delivery',
  'lock_time', 'tags', 'tag_categories', 'atag',
];

interface Props {
  detail: NotificationDetail;
  meta: Partial<NotificationCore>;
  setMeta: NotificationDraft['setMeta'];
  /** Meta field errors from mapNotificationErrors().meta. */
  errors: Record<string, string>;
  canManage: boolean;
  tenantId: number | 'system';
}

export function NotificationRulePanel({ detail, meta, setMeta, errors, canManage, tenantId }: Props) {
  const nf = detail.notification;
  const refs = detail.refs;
  const isSystem = tenantId === 'system';

  const [expanded, setExpanded] = useState(false);
  // A rejected save must never hide its errors inside a collapsed accordion.
  useEffect(() => {
    if (TARGETING_ERROR_KEYS.some((key) => errors[key])) setExpanded(true);
  }, [errors]);

  // ---- language policy (unchanged pattern) ---------------------------------
  const [policyOpt, setPolicyOpt] = useState<PolicyOption | null>(refs.language_policy);
  useEffect(() => {
    const current = meta.language_policy ?? null;
    setPolicyOpt((prev) => {
      if ((prev?.id ?? null) === current) return prev;
      if (current == null) return null;
      if (refs.language_policy?.id === current) return refs.language_policy;
      return { id: current, codename: `policy #${current}` };
    });
  }, [meta.language_policy, refs.language_policy]);

  const fetchPolicies = useCallback(
    (q: string): Promise<PolicyOption[]> =>
      languagePoliciesApi
        .list({ search: q, pageSize: 20 })
        .then((r) => r.results.map((p) => ({ id: p.id, codename: p.codename }))),
    [],
  );

  // ---- lookups --------------------------------------------------------------
  const eventsQuery = useQuery({
    queryKey: ['nf-events'],
    queryFn: () => notificationsApi.listEvents(),
    staleTime: 30 * 60_000,
  });
  const eventOptions = eventsQuery.data?.events ?? [];

  const rolesQuery = useQuery({
    queryKey: ['nf-employee-roles', tenantId],
    queryFn: () => rbacApi.listEmployeeRoles(tenantId as number),
    enabled: !isSystem,
    staleTime: 5 * 60_000,
  });
  const roleOptions: IdOption[] = useMemo(
    () => (rolesQuery.data?.results ?? []).map((r) => ({ id: r.id, name: r.name })),
    [rolesQuery.data],
  );

  const roles = useLabelledIds(meta.roles, refs.roles);
  const tags = useLabelledIds(meta.tags, refs.tags);
  const atags = useLabelledIds(meta.atag, refs.atag);
  const tours = useLabelledIds(meta.tours, refs.tours);

  const fetchTags = useCallback(
    async (q: string): Promise<IdOption[]> => {
      const res = await tagsApi.list({
        search: q || undefined,
        ...(tenantId !== 'system' ? { tour_operator_id: tenantId } : {}),
        pageSize: 50,
      });
      return res.results.map((t) => ({ id: t.id, name: `${t.category.code}: ${t.name}` }));
    },
    [tenantId],
  );

  const fetchAtags = useCallback(
    async (q: string): Promise<IdOption[]> => {
      const res = await rbacApi.listAccessTags(tenantId as number, q);
      return res.results.map((t) => ({ id: t.id, name: t.name }));
    },
    [tenantId],
  );

  const fetchTours = useCallback(
    async (q: string): Promise<IdOption[]> => {
      const res = await toursApi.list({
        search: q || undefined,
        ...(tenantId !== 'system' ? { tour_operator_id: tenantId } : {}),
        pageSize: 50,
      });
      return res.results.map((t) => ({
        id: t.id,
        name: t.is_bespoke
          ? `${t.name} (${t.start_date ?? '?'} - ${t.end_date ?? '?'})`
          : `${t.name} (Recurring)`,
      }));
    },
    [tenantId],
  );

  const pickIds = useCallback(
    (key: 'roles' | 'tags' | 'atag' | 'tours', learn: (o: IdOption[]) => void) =>
      (v: IdOption[] | IdOption | null) => {
        const options = (v as IdOption[] | null) ?? [];
        learn(options);
        setMeta(key, options.map((o) => o.id));
      },
    [setMeta],
  );

  // Grid links are scoped to the owning operator; a system template has none.
  const linkCtx = useMemo(
    () => ({ tenantId: isSystem ? null : (tenantId as number) }),
    [isSystem, tenantId],
  );

  const summaryParts = useMemo(() => targetingSummary(meta, refs), [meta, refs]);
  const showBookingState = eventHasBookingState(meta.event);
  const overrideMode = Number(meta.tour_override_mode ?? 1);
  const lockOn = Number(meta.lock_delivery ?? 0) === 1;
  const fieldError = (key: string) =>
    errors[key] ? (
      <Typography variant="caption" color="error">
        {errors[key]}
      </Typography>
    ) : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="Name"
            size="small"
            value={meta.name ?? ''}
            onChange={(e) => setMeta('name', e.target.value)}
            disabled={!canManage}
            error={Boolean(errors.name)}
            helperText={errors.name}
            sx={{ flex: 2, minWidth: 260 }}
          />
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <RemoteAutocomplete<PolicyOption>
              size="small"
              label="Language policy"
              placeholder="Standard resolution"
              queryFn={fetchPolicies}
              getOptionLabel={(p) => p.codename}
              getOptionKey={(p) => p.id}
              value={policyOpt}
              onChange={(v) => {
                if (!canManage) return;
                const opt = v as PolicyOption | null;
                setPolicyOpt(opt);
                setMeta('language_policy', opt?.id ?? null);
              }}
              fetchOnEmpty
            />
            {fieldError('language_policy')}
            {policyOpt && (
              <PickerLinks
                items={[
                  {
                    key: policyOpt.id,
                    label: policyOpt.codename,
                    actions: languagePolicyActions(policyOpt),
                  },
                ]}
              />
            )}
          </Box>
        </Stack>

        <Accordion
          variant="outlined"
          disableGutters
          expanded={expanded}
          onChange={(_, open) => setExpanded(open)}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ minWidth: 0, pr: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Targeting
              </Typography>
              {!expanded &&
                summaryParts.map((part) => (
                  <Chip
                    key={part.text}
                    size="small"
                    variant="outlined"
                    color={part.tone ?? 'default'}
                    label={part.text}
                    sx={{ height: 22, fontSize: 12, maxWidth: 280 }}
                  />
                ))}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Box sx={{ flex: 2, minWidth: 280 }}>
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <Autocomplete<notificationsApi.NotificationEvent | string, false, false, true>
                      freeSolo
                      size="small"
                      fullWidth
                      disabled={!canManage}
                      options={eventOptions}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : o.code)}
                      value={meta.event ?? ''}
                      onChange={(_, v) => {
                        if (v == null) return;
                        setMeta('event', typeof v === 'string' ? v : v.code);
                      }}
                      onInputChange={(_, v, reason) => {
                        if (reason === 'input' || reason === 'clear') setMeta('event', v);
                      }}
                      renderOption={(props, option) => {
                        const { key, ...rest } = props as { key?: string } & typeof props;
                        const o = option as notificationsApi.NotificationEvent;
                        return (
                          <li key={key ?? o.code} {...rest}>
                            <Stack sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {o.code}
                              </Typography>
                              {o.label && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {o.label}
                                </Typography>
                              )}
                            </Stack>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Event"
                          error={Boolean(errors.event)}
                          helperText={
                            errors.event ??
                            'Changing the event changes when this fires and which variables templates see.'
                          }
                        />
                      )}
                    />
                    <FieldHelp help={TARGETING_HELP.event} />
                  </Stack>
                </Box>
                {showBookingState && (
                  <TextField
                    select
                    size="small"
                    label="Booking state"
                    sx={{ width: 220 }}
                    value={Number(meta.booking_state ?? 1)}
                    disabled={!canManage}
                    error={Boolean(errors.booking_state)}
                    helperText={errors.booking_state}
                    onChange={(e) => setMeta('booking_state', Number(e.target.value))}
                    InputProps={{ endAdornment: <FieldHelp help={TARGETING_HELP.bookingState} /> }}
                  >
                    {Object.entries(BOOKING_STATE_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={Number(value)}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Autocomplete<IdOption, true>
                    multiple
                    size="small"
                    disabled={!canManage || isSystem}
                    options={roleOptions}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={roles.value}
                    onChange={(_, v) => pickIds('roles', roles.learn)(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Triggered by (staff roles)"
                        error={Boolean(errors.roles)}
                        helperText={
                          errors.roles ??
                          (isSystem ? 'Roles are operator-scoped — unavailable on system templates.' : undefined)
                        }
                      />
                    )}
                  />
                  <PickerLinks
                    items={roles.value.map((r) => ({
                      key: r.id,
                      label: r.name,
                      actions: roleActions(r, linkCtx),
                    }))}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(meta.apply_to_guests)}
                      disabled={!canManage}
                      onChange={(e) => setMeta('apply_to_guests', e.target.checked)}
                    />
                  }
                  label="Guests"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(meta.apply_to_observers)}
                      disabled={!canManage}
                      onChange={(e) => setMeta('apply_to_observers', e.target.checked)}
                    />
                  }
                  label={labelWithHelp(
                    <Typography variant="body2">Observers</Typography>,
                    TARGETING_HELP.triggeredBy,
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  size="small"
                  label="Tour override mode"
                  sx={{ width: 300 }}
                  value={overrideMode}
                  disabled={!canManage}
                  error={Boolean(errors.tour_override_mode)}
                  helperText={errors.tour_override_mode}
                  onChange={(e) => setMeta('tour_override_mode', Number(e.target.value))}
                  InputProps={{ endAdornment: <FieldHelp help={TARGETING_HELP.tourOverrideMode} /> }}
                >
                  {Object.entries(TOUR_OVERRIDE_MODE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={Number(value)}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                {overrideMode === TOUR_SPECIFIC_MODE &&
                  (isSystem ? (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      Tour-specific mode needs an owning operator — tours cannot be picked on a
                      system template.
                    </Typography>
                  ) : (
                    <Box sx={{ flex: 1, minWidth: 280 }}>
                      <RemoteAutocomplete<IdOption>
                        multiple
                        size="small"
                        disabled={!canManage}
                        label="Tours"
                        placeholder={tours.value.length ? '' : 'Fires only for these tours'}
                        queryFn={fetchTours}
                        getOptionLabel={(o) => o.name}
                        getOptionKey={(o) => o.id}
                        value={tours.value}
                        onChange={pickIds('tours', tours.learn)}
                        fetchOnEmpty
                      />
                      {fieldError('tours')}
                      <PickerLinks
                        items={tours.value.map((t) => ({
                          key: t.id,
                          label: t.name,
                          actions: tourActions(t, linkCtx),
                        }))}
                      />
                    </Box>
                  ))}
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={lockOn}
                      disabled={!canManage}
                      onChange={(e) => setMeta('lock_delivery', e.target.checked ? 1 : 0)}
                    />
                  }
                  label={labelWithHelp(
                    <Typography variant="body2">Block repeat delivery to the same user</Typography>,
                    TARGETING_HELP.deliveryLock,
                  )}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Window (seconds)"
                  sx={{ width: 170 }}
                  value={Number(meta.lock_time ?? 300)}
                  disabled={!canManage || !lockOn}
                  error={Boolean(errors.lock_time)}
                  helperText={errors.lock_time}
                  inputProps={{ min: 0, max: MAX_LOCK_TIME }}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(MAX_LOCK_TIME, Number(e.target.value) || 0));
                    setMeta('lock_time', v);
                  }}
                />
                {lockOn && (
                  <Typography variant="caption" color="text.secondary">
                    at most once per {humanizeSeconds(Number(meta.lock_time ?? 0))}; extra deliveries
                    are logged as rejected
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(meta.ignore_user_nf_prefs)}
                      disabled={!canManage}
                      onChange={(e) => setMeta('ignore_user_nf_prefs', e.target.checked)}
                    />
                  }
                  label={labelWithHelp(
                    <Typography variant="body2" color={meta.ignore_user_nf_prefs ? 'warning.main' : undefined}>
                      Ignore user notification preferences
                    </Typography>,
                    TARGETING_HELP.ignoreUserPrefs,
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <RemoteAutocomplete<IdOption>
                    multiple
                    size="small"
                    disabled={!canManage}
                    label="Tags"
                    queryFn={fetchTags}
                    getOptionLabel={(o) => o.name}
                    getOptionKey={(o) => o.id}
                    value={tags.value}
                    onChange={pickIds('tags', tags.learn)}
                    fetchOnEmpty
                  />
                  {fieldError('tags')}
                  <PickerLinks
                    items={tags.value.map((t) => ({
                      key: t.id,
                      label: t.name,
                      // The option label already carries "<category>: <name>",
                      // but not the category ID — the category actions are
                      // offered on the tag page itself.
                      actions: tagChipActions(
                        { id: t.id, name: t.name, category: null },
                        { tenantId: linkCtx.tenantId, owner: 'booking' },
                      ),
                    }))}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <Box sx={{ flex: 1 }}>
                      <RemoteAutocomplete<IdOption>
                        multiple
                        size="small"
                        disabled={!canManage || isSystem}
                        label="Access tags"
                        placeholder={isSystem ? 'Operator-scoped — unavailable on system templates' : ''}
                        queryFn={fetchAtags}
                        getOptionLabel={(o) => o.name}
                        getOptionKey={(o) => o.id}
                        value={atags.value}
                        onChange={pickIds('atag', atags.learn)}
                        fetchOnEmpty
                      />
                      {fieldError('atag')}
                    </Box>
                    <FieldHelp help={TARGETING_HELP.accessTags} />
                  </Stack>
                </Box>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Timing: {timedeltaLabel(nf.timedelta)}.
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Paper>
  );
}
